import { useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/app/PageBits";
import { Button } from "@/components/ui/button";
import { Upload, Download, CheckCircle2, AlertCircle, SkipForward } from "lucide-react";
import { toast } from "sonner";

const UploadCard = ({ title, subtitle, endpoint, templateEndpoint, sampleCols, testIdPrefix }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(endpoint, form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`Imported: ${r.data.created.length} created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const dlTemplate = async () => {
    try {
      const r = await api.get(templateEndpoint, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = templateEndpoint.split("/").pop(); document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch { toast.error("Template download failed"); }
  };

  return (
    <div className="border border-border rounded-md bg-card p-6" data-testid={`${testIdPrefix}-card`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-heading text-lg font-bold tracking-tight">{title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
        </div>
        <Button data-testid={`${testIdPrefix}-template`} variant="outline" size="sm" onClick={dlTemplate}>
          <Download className="h-4 w-4 mr-2"/>Template
        </Button>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Required columns</div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {sampleCols.map((c) => (
          <span key={c} className="text-[10px] font-mono px-2 py-0.5 border border-border rounded bg-muted/60">{c}</span>
        ))}
      </div>
      <label className="block cursor-pointer border-2 border-dashed border-border rounded-md p-6 text-center hover:border-primary/50 transition-colors">
        <input data-testid={`${testIdPrefix}-file`} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" strokeWidth={1.5}/>
        <div className="text-sm font-medium">{file ? file.name : "Click to choose a .csv file"}</div>
        <div className="text-xs text-muted-foreground mt-1">{file ? `${(file.size / 1024).toFixed(1)} KB` : "or drag and drop"}</div>
      </label>
      <div className="mt-4 flex gap-2">
        <Button data-testid={`${testIdPrefix}-upload`} onClick={upload} disabled={!file || busy}>{busy ? "Uploading…" : "Upload & import"}</Button>
        {file && <Button data-testid={`${testIdPrefix}-clear`} variant="ghost" onClick={() => { setFile(null); setResult(null); }}>Clear</Button>}
      </div>

      {result && (
        <div className="mt-6 border-t border-border pt-5" data-testid={`${testIdPrefix}-result`}>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="p-3 border border-border rounded-md text-center"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total rows</div><div className="font-heading text-xl font-bold tabular">{result.total_rows}</div></div>
            <div className="p-3 border border-green-200 bg-green-50 rounded-md text-center"><div className="text-[10px] uppercase tracking-widest text-green-800">Created</div><div className="font-heading text-xl font-bold tabular text-green-800">{result.created.length}</div></div>
            <div className="p-3 border border-amber-200 bg-amber-50 rounded-md text-center"><div className="text-[10px] uppercase tracking-widest text-amber-800">Skipped</div><div className="font-heading text-xl font-bold tabular text-amber-800">{result.skipped.length}</div></div>
            <div className="p-3 border border-red-200 bg-red-50 rounded-md text-center"><div className="text-[10px] uppercase tracking-widest text-red-800">Errors</div><div className="font-heading text-xl font-bold tabular text-red-800">{result.errors.length}</div></div>
          </div>
          <div className="max-h-56 overflow-auto space-y-1 text-xs">
            {result.errors.map((e, i) => (
              <div key={`e${i}`} className="flex gap-2 items-center text-red-800"><AlertCircle className="h-3 w-3"/>Row {e.row}: {e.reason}</div>
            ))}
            {result.skipped.map((s, i) => (
              <div key={`s${i}`} className="flex gap-2 items-center text-amber-800"><SkipForward className="h-3 w-3"/>Row {s.row}: {s.reason || s.email || "skipped"}</div>
            ))}
            {result.created.slice(0, 20).map((c, i) => (
              <div key={`c${i}`} className="flex gap-2 items-center text-green-800"><CheckCircle2 className="h-3 w-3"/>Row {c.row}: created {c.email || c.plot_no}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Imports() {
  return (
    <div>
      <PageHeader
        title="Bulk import"
        subtitle="Onboard whole projects in one go. Upload a CSV of customers first, then plots. Existing customers by email and existing plots (project, plot_no) are skipped, never overwritten."
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UploadCard
          title="Customers"
          subtitle="One row per customer. Email is the unique key and doubles as the Google sign-in address."
          endpoint="/imports/customers"
          templateEndpoint="/imports/templates/customers.csv"
          sampleCols={["full_name","email","phone","pan","aadhaar","address","title"]}
          testIdPrefix="imp-customers"
        />
        <UploadCard
          title="Plots"
          subtitle="One row per plot. owner_email must match an existing customer, project_name must match an existing project."
          endpoint="/imports/plots"
          templateEndpoint="/imports/templates/plots.csv"
          sampleCols={["project_name","plot_no","area_sqft","owner_email","maintenance_plan","billing_cycle","start_date"]}
          testIdPrefix="imp-plots"
        />
      </div>
    </div>
  );
}
